import type { U3129HonorariumItem } from "../U3129Service";
import {
  type U3129HonorariumProjectGroup,
  u3129HonorariumRowIncludeSubProject,
  u3129ResolveHonorariumProjectKey,
} from "./u3129GroupHonorariumForDisplay";
import {
  u3129HonorariumTemplateScopeKey,
  type U3129HonorariumTemplateScopeResolution,
} from "./u3129HonorariumTemplate";

export type U3129HonorariumCoherenceMessages = {
  projectInactive: string;
  taskNotAvailable: string;
  subprojectRequired: string;
};

export type U3129HonorariumCoherenceWarningsState = {
  projectByKey: Map<string, string>;
  taskByLineKey: Map<string, string>;
  hasAny: boolean;
};

/** Clé métier stable (survit au POST update sans clientRowId). */
export function u3129HonorariumLineIdentityKey(row: U3129HonorariumItem): string | null {
  const projectId = resolveProjectId(row);
  if (!projectId) {
    return null;
  }
  const scope = u3129HonorariumTemplateScopeKey({
    projectId,
    subprojectId: resolveSubprojectId(row),
  });
  const task = resolveTaskType(row);
  return `${scope}|${task || "no-task"}`;
}

export function u3129HonorariumCoherenceLineKey(row: U3129HonorariumItem, index: number): string {
  const identity = u3129HonorariumLineIdentityKey(row);
  if (identity) {
    return identity;
  }
  return row.clientRowId?.trim() || `idx-${index}`;
}

/** Réattache les clientRowId UI après normalisation GET/update (sinon triangles disparaissent). */
export function u3129PreserveHonorariumClientRowIds(
  previous: U3129HonorariumItem[] | undefined,
  next: U3129HonorariumItem[],
): U3129HonorariumItem[] {
  const idsByIdentity = new Map<string, string>();
  for (const row of previous ?? []) {
    const identity = u3129HonorariumLineIdentityKey(row);
    const cid = row.clientRowId?.trim();
    if (identity && cid) {
      idsByIdentity.set(identity, cid);
    }
  }
  return next.map((row) => {
    const identity = u3129HonorariumLineIdentityKey(row);
    const preserved = identity ? idsByIdentity.get(identity) : undefined;
    if (preserved) {
      return { ...row, clientRowId: preserved };
    }
    return row;
  });
}

function resolveProjectId(row: U3129HonorariumItem): string {
  return row.projectId?.trim() || row.project?.id?.trim() || "";
}

function resolveSubprojectId(row: U3129HonorariumItem): string {
  const sid = row.subprojectId?.trim();
  if (sid) return sid;
  const sp = row.subproject as { id?: string } | undefined;
  return sp?.id?.trim() ?? "";
}

function resolveTaskType(row: U3129HonorariumItem): string {
  return row.taskType?.trim() || row.task?.id?.trim() || "";
}

/** Corps findTasksForProject distincts pour les lignes honoraires d'une feuille. */
export function u3129UniqueFindTasksBodiesFromHonorariumRows(
  rows: U3129HonorariumItem[],
): Array<{ scopeKey: string; body: { projectId: string; subprojectId: string | null } }> {
  const seen = new Map<string, { projectId: string; subprojectId: string | null }>();
  for (const row of rows) {
    const projectId = resolveProjectId(row);
    if (!projectId) continue;
    const scopeKey = u3129HonorariumTemplateScopeKey({
      projectId,
      subprojectId: resolveSubprojectId(row),
    });
    if (seen.has(scopeKey)) continue;
    const sub = resolveSubprojectId(row);
    seen.set(scopeKey, { projectId, subprojectId: sub ? sub : null });
  }
  return [...seen.entries()].map(([scopeKey, body]) => ({ scopeKey, body }));
}

export function u3129BuildHonorariumCoherenceWarnings(
  rows: U3129HonorariumItem[],
  resolutions: Map<string, U3129HonorariumTemplateScopeResolution>,
  messages: U3129HonorariumCoherenceMessages,
  admIncludeSubProjectEnabled: boolean,
): U3129HonorariumCoherenceWarningsState {
  const projectByKey = new Map<string, string>();
  const taskByLineKey = new Map<string, string>();

  rows.forEach((row, index) => {
    const projectId = resolveProjectId(row);
    if (!projectId) return;

    const projectKey = u3129ResolveHonorariumProjectKey(row);
    const lineKey = u3129HonorariumCoherenceLineKey(row, index);
    const scopeKey = u3129HonorariumTemplateScopeKey({
      projectId,
      subprojectId: resolveSubprojectId(row),
    });

    const needsSub =
      admIncludeSubProjectEnabled &&
      u3129HonorariumRowIncludeSubProject(row) &&
      !resolveSubprojectId(row);
    if (needsSub) {
      taskByLineKey.set(lineKey, messages.subprojectRequired);
      if (!projectByKey.has(projectKey)) {
        projectByKey.set(projectKey, messages.subprojectRequired);
      }
      return;
    }

    const resolution = resolutions.get(scopeKey);
    if (!resolution || resolution.status === "unusable") {
      if (!projectByKey.has(projectKey)) {
        projectByKey.set(projectKey, messages.projectInactive);
      }
      const taskType = resolveTaskType(row);
      if (taskType) {
        taskByLineKey.set(lineKey, messages.projectInactive);
      }
      return;
    }

    const taskType = resolveTaskType(row);
    if (!taskType) return;
    if (!resolution.taskIds.has(taskType)) {
      taskByLineKey.set(lineKey, messages.taskNotAvailable);
    }
  });

  return {
    projectByKey,
    taskByLineKey,
    hasAny: projectByKey.size > 0 || taskByLineKey.size > 0,
  };
}

export function u3129EmptyHonorariumCoherenceWarnings(): U3129HonorariumCoherenceWarningsState {
  return { projectByKey: new Map(), taskByLineKey: new Map(), hasAny: false };
}

/**
 * Triangle ligne projet : erreur projet toujours ; erreur tâche seulement si le projet est replié.
 */
export function u3129HonorariumCoherenceMessageForProjectGroup(
  group: U3129HonorariumProjectGroup,
  warnings: U3129HonorariumCoherenceWarningsState | null | undefined,
  options?: { projectExpanded?: boolean },
): string {
  if (!warnings?.hasAny) {
    return "";
  }
  const direct = warnings.projectByKey.get(group.projectKey)?.trim();
  if (direct) {
    return direct;
  }
  if (options?.projectExpanded === true) {
    return "";
  }
  for (const { index, row } of group.allLines) {
    const taskMsg = warnings.taskByLineKey.get(u3129HonorariumCoherenceLineKey(row, index))?.trim();
    if (taskMsg) {
      return taskMsg;
    }
  }
  return "";
}
